import { useSnackbar } from '@/contexts/SnackBarContext';
import { SubAccountDto } from '@/models/suba/SubAccountDTO.model';
import SubAccountService from '@/services/suba/accounts/SubAccount.service';
import BalanceService from '@/services/suba/balances/Balance.service';
import formatters from '@/utils/Formatters';
import { testDecimals } from '@/utils/validations/helpers/YupValidationFunctions';
import SearchIcon from '@mui/icons-material/Search';
import {
  Button,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { OperationalSubAccountList } from '@vestwell-sub-accounting/models/accountsAndLedgers/OperationalSubAccountList';

import { AxiosError } from 'axios';
import { Field, useFormikContext } from 'formik';
import { kebabCase } from 'lodash';
import { FC, useEffect, useMemo } from 'react';

import { SubAccountBalances } from '../SubAccountBalances';
import {
  alloweWithdrawalCodes,
  CreateDisbursementRequestFormValues
} from './CreateDisbursementRequestDialog.component';

type CreateDisbursementRequestStep1Props = {
  active: boolean;
  onEdit: () => void;
  onContinue: () => void;
  parentAccountId?: string;
  planId: string;
  subAccount?: SubAccountDto;
};

export const CreateDisbursementRequestStep1: FC<
  CreateDisbursementRequestStep1Props
> = props => {
  const { showSnackbar } = useSnackbar();

  const searchSubAccountsQuery = useQuery(
    [
      'SubAccountService.searchSubAccounts',
      {
        hasPlanId: Boolean(props.planId),
        planId: props.planId
      }
    ],
    () =>
      SubAccountService.searchSubAccounts({
        accountType: OperationalSubAccountList,
        hasPlanId: Boolean(props.planId),
        page: 1,
        pageSize: 25,
        planId: props.planId
      }),
    {
      enabled: props.planId !== undefined && !props.subAccount,
      onError: (err: AxiosError) => {
        showSnackbar({
          message: `Failed to fetch operational sub accounts: ${err.response?.data ? err.response.data : err.message}`,
          severity: 'error'
        });
      }
    }
  );

  const formik = useFormikContext<CreateDisbursementRequestFormValues>();

  useEffect(() => {
    formik.validateForm(formik.initialValues).then(errors => {
      if (!errors.step1) {
        props.onContinue();
      }
    });
  }, [formik.initialValues]);

  const subAccount = useMemo(() => {
    if (props.subAccount) {
      return props.subAccount;
    }

    return searchSubAccountsQuery.data?.results.find(
      subAccount => subAccount.subAccountId === formik.values.step1.subAccountId
    );
  }, [
    formik.values.step1.subAccountId,
    searchSubAccountsQuery.data,
    props.parentAccountId,
    props.subAccount
  ]);

  const fromSubAccountBalancesQuery = useQuery(
    ['BalancesService.get', subAccount?.subAccountId],
    () => BalanceService.get(subAccount.subAccountId),
    {
      enabled: Boolean(subAccount?.subAccountId)
    }
  );

  const validateGrossAmount = (value: number) => {
    let error;
    if (!fromSubAccountBalancesQuery.data) return;

    if (!testDecimals(2)(value)) {
      error = 'Gross Amount must have 2 decimal places';
    }
    if (value > Number(fromSubAccountBalancesQuery.data.cash.confirmed)) {
      error = 'Gross Amount cannot exceed confirmed balance';
    }
    return error;
  };

  return (
    <>
      {!props.active && subAccount !== undefined ? (
        <>
          <Stack sx={{ pt: 2 }}>
            <Typography variant='body2'>
              Gross Amount:{' '}
              {formatters.formatDollars(formik.values.step1.grossAmount, 2)}
            </Typography>
            <Typography variant='body2'>
              From: {formatters.formatSubAccountName(subAccount)}
            </Typography>
            <Typography variant='body2'>
              Withdrawal Code:{' '}
              {formatters.displayCase(formik.values.step1.withdrawalCode)}
            </Typography>
          </Stack>
          <Button data-testid='edit-button' onClick={props.onEdit}>
            Edit
          </Button>
        </>
      ) : (
        <>
          <Stack direction='row' spacing={3}>
            <Stack flex={1} spacing={2}>
              <FormControl fullWidth size='small'>
                <InputLabel htmlFor='sub-account-select'>
                  Sub Account
                </InputLabel>
                <Select
                  IconComponent={SearchIcon}
                  data-testid='sub-account-select'
                  disabled={
                    searchSubAccountsQuery.isLoading || !!props.subAccount
                  }
                  id='sub-account-select'
                  label='Sub Account'
                  name='step1.subAccountId'
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      transform: 'none'
                    }
                  }}
                  value={formik.values.step1.subAccountId}>
                  {props.subAccount && (
                    <MenuItem
                      key={props.subAccount.subAccountId}
                      value={props.subAccount.subAccountId}>
                      {formatters.formatSubAccountName(props.subAccount)}
                    </MenuItem>
                  )}
                  {searchSubAccountsQuery.data?.results.map(subAccountOpt => (
                    <MenuItem
                      key={subAccountOpt.subAccountId}
                      value={subAccountOpt.subAccountId}>
                      {formatters.formatSubAccountName(subAccountOpt)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <SubAccountBalances
                subAccountId={formik.values.step1.subAccountId}
              />
              <FormControl fullWidth size='small'>
                <InputLabel htmlFor='withdrawal-code'>
                  Withdrawal Code
                </InputLabel>
                <Field
                  as={Select}
                  inputProps={{
                    'data-testid': 'withdrawal-code',
                    id: 'withdrawal-code'
                  }}
                  label='Withdrawal Code'
                  name='step1.withdrawalCode'
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.step1.withdrawalCode}>
                  {alloweWithdrawalCodes.sort().map(value => (
                    <MenuItem
                      key={`withdrawal-code-${kebabCase(value)}`}
                      value={value}>
                      {formatters.displayCase(value)}
                    </MenuItem>
                  ))}
                </Field>
                <FormHelperText>Withdrawal, Income, or Fee type</FormHelperText>
              </FormControl>
            </Stack>
            <Stack flex={1} spacing={2}>
              <FormControl fullWidth size='small'>
                <InputLabel htmlFor='gross-amount'>Gross Amount</InputLabel>
                <Field
                  as={OutlinedInput}
                  error={
                    formik.errors.step1?.grossAmount &&
                    formik.touched.step1?.grossAmount
                  }
                  inputProps={{
                    'data-testid': 'gross-amount',
                    id: 'gross-amount',
                    min: '0',
                    step: '0.01'
                  }}
                  label='Gross Amount'
                  name='step1.grossAmount'
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  startAdornment={
                    <InputAdornment position='start'>$</InputAdornment>
                  }
                  type='number'
                  validate={validateGrossAmount}
                  value={formik.values.step1.grossAmount}
                />
                <FormHelperText
                  error={
                    formik.errors.step1?.grossAmount &&
                    formik.touched.step1?.grossAmount
                  }>
                  {formik.touched.step1?.grossAmount &&
                    formik.errors.step1?.grossAmount}
                </FormHelperText>
              </FormControl>
              <FormControl fullWidth size='small'>
                <InputLabel htmlFor='ticket-id'>Ticket ID</InputLabel>
                <Field
                  as={OutlinedInput}
                  inputProps={{
                    'data-testid': 'ticket-id',
                    id: 'ticket-id'
                  }}
                  label='Ticket ID'
                  name='step1.ticketId'
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.step1.ticketId}
                />
                <FormHelperText>Add relevant Jira ticket ID.</FormHelperText>
              </FormControl>
              <FormControl fullWidth size='small'>
                <InputLabel htmlFor='note'>Note</InputLabel>
                <OutlinedInput
                  data-testid='note'
                  id='note'
                  label='Note'
                  multiline
                  name='step1.note'
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  rows={3}
                  value={formik.values.step1.note}
                />
                <FormHelperText>Optional. Do not enter PII.</FormHelperText>
              </FormControl>
            </Stack>
          </Stack>
          <Button
            data-testid='continue-button'
            disabled={!!formik.errors.step1}
            onClick={props.onContinue}
            variant='contained'>
            Continue
          </Button>
        </>
      )}
    </>
  );
};

export default CreateDisbursementRequestStep1;
